/**
 * Staff redeem — `/redeem` (routes/public.jsx patronFullscreenRoutes: PortalLayout, no site
 * chrome, both SLOT_MODEL states). Staff type the code a guest shows them.
 *
 * Behaviour (unchanged by the redesign; ADR-0014: redeeming needs a staff login):
 * - who may redeem: a business owner, a venue partner or an admin (lib/roles guards); anyone
 *   else — signed out, or a patron — sees "Staff sign-in required" and a Sign in button that
 *   opens the login modal and comes back here (setPostAuthRedirect('/redeem'));
 * - POST /api/redeem?code=<CODE> through businessApi (upper-cased, trimmed); 200 → the
 *   business name and the offer (`business_name`, `deal_title`; an empty one is left out); 401 → back to the sign-in panel; 404 "Invalid code…"; 403 the
 *   server's detail (another business's code); 400 "already redeemed"; else a generic error;
 * - analytics: trackDealRedeem on success.
 *
 * Look (site redesign Phase 6, components/site on the brand tokens; replaces the V5 palette
 * and inline styles): the paper canvas with a slim logo bar, one <main id="main"> with the one
 * h1 "Redeem a code", then one card per state — sign-in (navy top edge), the code form (orange
 * top edge; the code in a large IBM Plex Mono field), the result (navy edge) or the problem
 * (rust edge). A result or a problem takes focus (its heading) and is announced.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { Button, Eyebrow, Heading, SiteCard, TextLink } from '../components/site';
import { businessApi, errorMessage } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { hasBusinessAccess, hasVenueAccess, hasAdminAccess } from '../components/routing/guards';
import { primaryDashboardPath } from '../lib/roles';
import { trackDealRedeem } from '../utils/analytics';
import { cn } from '../lib/utils';

// Staff-only: POST /api/redeem requires a business-owner or venue-partner login
// (401 signed out, 403 when the code belongs to another business).
const canRedeem = (user) => hasBusinessAccess(user) || hasVenueAccess(user) || hasAdminAccess(user);

export const CODE_LENGTH = 6;

/**
 * Cards on a narrow screen with enlarged text (under 20em: 320px at 200%) keep their padding
 * to 1rem, so headings and button labels keep enough room not to break mid-word.
 */
const TIGHT_CARD = '[@media(max-width:19.9375em)]:p-4';

/** A trimmed string from an API field ('' for anything else). */
const textOf = (v) => (typeof v === 'string' ? v.trim() : '');

// 740px reading column with the page gutter.
const COLUMN = 'mx-auto w-full max-w-[46.25rem] px-gutter';

/** The heading of a result card; it takes focus when the card appears (see RESULT_TITLE_ID). */
const RESULT_TITLE_ID = 'redeem-result-title';
function CardTitle({ children }) {
  return (
    <Heading level={2} size="card" id={RESULT_TITLE_ID} tabIndex={-1} className="m-0 outline-none">
      {children}
    </Heading>
  );
}

const Redeem = () => {
  const { user, loading: authLoading, openAuthModal, setPostAuthRedirect } = useAuth();
  const [authRequired, setAuthRequired] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState('');
  const [businessInfo, setBusinessInfo] = useState(null);
  const inputRef = useRef(null);
  const focusInput = useRef(false);

  // A fresh login (user object changes) clears a stale 401.
  useEffect(() => {
    setAuthRequired(false);
  }, [user]);

  // A result or a problem: focus its heading. Back to the form: focus the code field.
  useEffect(() => {
    const title = status ? document.getElementById(RESULT_TITLE_ID) : null;
    if (title) title.focus();
    else if (!status && focusInput.current && inputRef.current) {
      focusInput.current = false;
      inputRef.current.focus();
    }
  }, [status]);

  const handleSignIn = () => {
    setPostAuthRedirect?.('/redeem');
    openAuthModal('login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!code.trim()) {
      setStatus('error');
      setMessage('Please enter a code');
      return;
    }

    setLoading(true);
    setStatus(null);

    try {
      const response = await businessApi.post('/redeem', null, {
        params: { code: code.toUpperCase().trim() }
      });

      // POST /api/redeem → { success, message, business_name, deal_title, deal_description }.
      const data = response.data || {};
      const info = { business_name: textOf(data.business_name), offer: textOf(data.deal_title) };
      setStatus('success');
      setMessage(data.message);
      setBusinessInfo(info.business_name || info.offer ? info : null);
      trackDealRedeem({ businessName: info.business_name, offer: info.offer, success: true });
      setCode('');
    } catch (error) {
      const httpStatus = error.response?.status;
      if (httpStatus === 401) {
        // Session missing or expired: back to the sign-in panel, drop any stale result.
        setStatus(null);
        setMessage('');
        setBusinessInfo(null);
        setAuthRequired(true);
        return;
      }
      setStatus('error');
      if (httpStatus === 404) {
        setMessage('Invalid code. Please check and try again.');
      } else if (httpStatus === 403) {
        setMessage(errorMessage(error, 'This code belongs to another business.'));
      } else if (httpStatus === 400) {
        setMessage('This code has already been redeemed.');
      } else {
        setMessage('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    focusInput.current = true;
    setStatus(null);
    setMessage('');
    setBusinessInfo(null);
    setCode('');
  };

  const dashboardPath = primaryDashboardPath(user);
  const staff = canRedeem(user) && !authRequired;

  return (
    <div className="site-paper site-light flex min-h-screen flex-col font-body text-navy">
      <SEO title="Redeem a code" description="Staff: redeem the offer code a guest shows you." url="/redeem" noIndex />
      <header className="border-b border-brand-rule bg-brand-sheet/80">
        <div className={cn(COLUMN, 'flex min-h-16 items-center justify-between gap-4 py-3')}>
          <Link to="/" aria-label="Tappd home" className="block shrink-0">
            <img src="/tappd-logo.png" alt="Tappd" width="114" height="39" className="block h-auto w-[96px] nav:w-[108px]" />
          </Link>
          <Eyebrow as="p" tone="muted" className="m-0">Staff</Eyebrow>
        </div>
      </header>

      <main id="main" className="flex-1 pb-section pt-10 nav:pt-16">
        <div className={COLUMN}>
          <Heading level={1} accent="rust" className="m-0">
            Redeem a <em>code</em>
          </Heading>
          <p className="mb-0 mt-5 max-w-[36.875rem] text-[1.0625rem] leading-[1.65] text-brand-muted nav:text-[1.1875rem]">
            Enter the code a guest shows you, on their screen or in their email. Each code works once.
          </p>

          <div className="mt-8 nav:mt-10">
            {authLoading ? (
              // Auth context still resolving: avoid flashing the sign-in panel
              <div role="status" aria-live="polite" data-testid="redeem-loading">
                <span className="sr-only">Loading</span>
                <div aria-hidden="true" className="h-56 border border-brand-rule bg-brand-sheet shadow-site-card">
                  <div className="m-6 h-4 w-40 bg-navy/10 motion-safe:animate-pulse" />
                  <div className="mx-6 h-16 bg-navy/10 motion-safe:animate-pulse" />
                </div>
              </div>
            ) : !staff ? (
              // Staff sign-in required
              <SiteCard as="section" aria-labelledby="redeem-signin-title" accent="navy" padding="lg" className={TIGHT_CARD} data-testid="redeem-signin-panel">
                <Eyebrow as="p" tone="rust" className="m-0">Staff only</Eyebrow>
                <Heading level={2} size="card" id="redeem-signin-title" className="mb-0 mt-3">
                  Staff sign-in required
                </Heading>
                <p className="mb-0 mt-3 text-base leading-relaxed text-brand-muted">
                  Redeem codes with the business or venue account that owns the deal.
                </p>
                <Button fullWidth className="mt-6" onClick={handleSignIn}>
                  Sign in
                </Button>
              </SiteCard>
            ) : status === null ? (
              // The code form
              <SiteCard as="section" aria-labelledby="redeem-form-title" accent="orange" padding="lg" className={TIGHT_CARD}>
                <h2 id="redeem-form-title" className="sr-only">Enter the code</h2>
                <form onSubmit={handleSubmit} aria-busy={loading || undefined}>
                  <label htmlFor="redeem-code" className="mb-2 block text-sm font-medium leading-snug">
                    Offer code
                  </label>
                  <input
                    ref={inputRef}
                    id="redeem-code"
                    name="code"
                    type="text"
                    autoComplete="off"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    enterKeyHint="go"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    maxLength={CODE_LENGTH}
                    readOnly={loading}
                    aria-describedby="redeem-code-hint"
                    className="block min-h-[4.25rem] w-full min-w-0 rounded-none border-2 border-navy bg-brand-sheet px-3 text-center font-label text-[1.75rem] font-medium uppercase tracking-[0.3em] text-navy read-only:cursor-progress nav:text-[2rem]"
                  />
                  <p id="redeem-code-hint" className="mb-0 mt-2 text-[0.8125rem] leading-snug text-brand-muted">
                    The {CODE_LENGTH}-character code from the guest&apos;s screen or email.
                  </p>
                  <Button
                    type="submit"
                    fullWidth
                    className="mt-6"
                    disabled={!code.trim()}
                    loading={loading}
                    loadingLabel="Checking…"
                  >
                    Redeem code
                  </Button>
                </form>
              </SiteCard>
            ) : status === 'success' ? (
              // Redeemed
              <div role="status" aria-live="polite">
                <SiteCard as="section" aria-labelledby={RESULT_TITLE_ID} accent="navy" padding="lg" className={TIGHT_CARD} data-testid="redeem-success">
                  <Eyebrow as="p" tone="rust" className="m-0">Done</Eyebrow>
                  <div className="mt-3">
                    <CardTitle>Code redeemed</CardTitle>
                  </div>
                  {businessInfo && (
                    <dl className="m-0 mt-6 grid gap-4 border-y border-dashed border-navy/40 bg-brand-rule/50 px-5 py-5" data-testid="redeem-details">
                      {businessInfo.business_name ? (
                        <div>
                          <dt className="font-label text-[0.6875rem] uppercase tracking-[0.07em] text-brand-muted">Business</dt>
                          <dd className="m-0 mt-1 break-words font-display text-[1.375rem] font-medium leading-tight">{businessInfo.business_name}</dd>
                        </div>
                      ) : null}
                      {businessInfo.offer ? (
                        <div>
                          <dt className="font-label text-[0.6875rem] uppercase tracking-[0.07em] text-brand-muted">Offer</dt>
                          <dd className="m-0 mt-1 break-words text-base leading-relaxed">{businessInfo.offer}</dd>
                        </div>
                      ) : null}
                    </dl>
                  )}
                  <p className="mb-0 mt-5 text-base leading-relaxed text-brand-muted">
                    The code is now marked as used and can&apos;t be redeemed again.
                  </p>
                  <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center">
                    <Button arrow="↺" onClick={handleReset}>
                      Redeem another code
                    </Button>
                    {dashboardPath ? (
                      <TextLink to={dashboardPath} className="self-start sm:self-auto">
                        Back to dashboard
                      </TextLink>
                    ) : null}
                  </div>
                </SiteCard>
              </div>
            ) : (
              // Not redeemed
              <SiteCard as="section" aria-labelledby={RESULT_TITLE_ID} accent="rust" padding="lg" className={TIGHT_CARD} data-testid="redeem-error">
                <div role="alert">
                  <CardTitle>That code didn&apos;t work</CardTitle>
                  <p className="mb-0 mt-3 text-base font-medium leading-relaxed text-brand-rust">{message}</p>
                </div>
                <Button arrow="↺" className="mt-6" onClick={handleReset}>
                  Try again
                </Button>
              </SiteCard>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Redeem;
